import { cn } from "@hub/lib/utils";
import { Monitor, Moon, Sun } from "lucide-react";
import { useTheme } from "next-themes";
import { useEffect, useState } from "react";

export const THEME_OPTIONS = [
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
  { value: "system", label: "System", icon: Monitor },
] as const;

/**
 * Light / Dark / System, as a three-segment control.
 *
 * The app uses a dropdown for this (src/components/layout/ThemeToggle.tsx); a
 * segmented control says the same thing in one glance and costs the hub no new
 * Radix dependency, which matters for a package that ships one page.
 *
 * Rendered as a placeholder until mounted. `theme` is read from localStorage on
 * the client, so drawing the real state during the first render would show
 * whichever segment happened to be the default and then visibly correct itself.
 */
export function ThemeToggle() {
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  const current = THEME_OPTIONS.find((o) => o.value === theme) ?? THEME_OPTIONS[2];

  return (
    // A fieldset rather than a div with role="group": same semantics, and the
    // native element is what assistive tech and the linter both expect. The
    // reset classes undo the UA's default margin/padding/min-width.
    <fieldset
      className="m-0 flex min-w-0 items-center gap-0.5 rounded-lg border border-border bg-card p-0.5"
      aria-label="Color theme"
    >
      {THEME_OPTIONS.map((option) => {
        const active = mounted && option.value === current.value;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => setTheme(option.value)}
            aria-label={option.label}
            aria-pressed={active}
            title={option.label}
            className={cn(
              "flex size-7 items-center justify-center rounded-md transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              active
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:bg-accent hover:text-foreground",
            )}
          >
            <option.icon className="size-4" />
          </button>
        );
      })}
    </fieldset>
  );
}
